import {
  AppstoreOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  ShoppingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Collapse } from 'antd';
import type { InventorySummary, Warehouse } from '@/types/inventory';
import styles from './InventorySummarySidebar.module.scss';

interface InventorySummarySidebarProps {
  summary?: InventorySummary;
  warehouse?: Warehouse;
  isLoading?: boolean;
  isFiltered?: boolean;
}

export function InventorySummarySidebar({
  summary,
  warehouse,
  isLoading,
  isFiltered = false,
}: InventorySummarySidebarProps) {
  const stats = [
    {
      key: 'totalStockItems',
      label: 'Total stock items',
      value: summary?.totalStockItems ?? 0,
      hint: isFiltered ? 'SKU lines matching filters' : 'SKU lines in warehouse',
      icon: <InboxOutlined />,
      tone: 'primary',
    },
    {
      key: 'totalStockQuantity',
      label: 'Product total stock',
      value: summary?.totalStockQuantity ?? 0,
      hint: isFiltered ? 'Units in filtered results' : 'Total units across products',
      icon: <ShoppingOutlined />,
      tone: 'accent',
    },
    {
      key: 'usedItems',
      label: 'In stock',
      value: summary?.usedItems ?? 0,
      hint: isFiltered ? 'Filtered lines with stock' : 'SKU lines with stock',
      icon: <CheckCircleOutlined />,
      tone: 'success',
    },
    {
      key: 'outOfStockItems',
      label: 'Out of stock',
      value: summary?.outOfStockItems ?? 0,
      hint: isFiltered ? 'Filtered lines at zero' : 'SKU lines at zero stock',
      icon: <StopOutlined />,
      tone: 'danger',
    },
  ];

  const collapseLabel = (
    <div className={styles.collapseLabel}>
      <span className={styles.collapseIcon}>
        <AppstoreOutlined />
      </span>
      <div className={styles.collapseText}>
        <span className={styles.collapseTitle}>Stock overview</span>
        {warehouse ? (
          <span className={styles.collapseSubtitle}>
            {warehouse.name} ({warehouse.code})
            {isFiltered ? ' · Filtered view' : ''}
          </span>
        ) : null}
      </div>
      {!isLoading && summary ? (
        <div className={styles.collapsePreview}>
          <span>{summary.totalStockItems.toLocaleString('en-IN')} items</span>
          <span className={styles.previewDot} aria-hidden />
          <span>{summary.totalStockQuantity.toLocaleString('en-IN')} units</span>
          <span className={styles.previewDot} aria-hidden />
          <span>{summary.totalProducts.toLocaleString('en-IN')} products</span>
        </div>
      ) : null}
    </div>
  );

  return (
    <Collapse
      bordered={false}
      className={styles.overviewCollapse}
      defaultActiveKey={[]}
      items={[
        {
          key: 'overview',
          label: collapseLabel,
          children: (
            <div className={styles.panelBody}>
              <div className={styles.statsGrid}>
                {stats.map((stat) => (
                  <div key={stat.key} className={`${styles.statCard} ${styles[stat.tone]}`}>
                    <div className={styles.statIcon}>{stat.icon}</div>
                    <div className={styles.statBody}>
                      <span className={styles.statLabel}>{stat.label}</span>
                      <strong className={styles.statValue}>
                        {isLoading ? '—' : stat.value.toLocaleString('en-IN')}
                      </strong>
                      <span className={styles.statHint}>{stat.hint}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}
